import React from 'react'
import type { Module } from '@/types/module'
import type { Lesson } from '@/types/lesson'

interface PublishModuleModalProps {
  module: Module
  lessons: Lesson[]
  onPublish: () => void
  onClose: () => void
  isPublishing: boolean
}

export const PublishModuleModal: React.FC<PublishModuleModalProps> = ({
  module,
  lessons,
  onPublish,
  onClose,
  isPublishing,
}) => {
  const videoLessons = lessons.filter((l) => l.contentType === 'video')
  const textLessons = lessons.filter((l) => l.contentType === 'text')
  const pdfLessons = lessons.filter((l) => l.contentType === 'pdf')

  const hasContent = lessons.length > 0

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-md rounded-[32px] bg-white dark:bg-surface-card border border-slate-200 dark:border-white/10 overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-300">
        {/* Header */}
        <div className="px-8 py-6 border-b border-slate-100 dark:border-white/5 flex items-center gap-4">
          <div className="size-12 rounded-2xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center">
            <span className="material-symbols-outlined text-[28px]">publish</span>
          </div>
          <div>
            <h3 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">Terbitkan Modul</h3>
            <p className="text-xs font-bold text-slate-400 tracking-widest mt-0.5">LMS • Kurikulum</p>
          </div>
        </div>

        {/* Content */}
        <div className="p-8">
          <div className="mb-8">
            <p className="text-sm font-bold text-slate-500 dark:text-slate-400 mb-2">Anda akan menerbitkan modul:</p>
            <div className="p-4 rounded-2xl bg-slate-50 dark:bg-white/[0.02] border border-slate-100 dark:border-white/5">
              <p className="text-slate-900 dark:text-white font-black text-lg tracking-tight leading-tight">{module.title}</p>
            </div>
          </div>

          {/* Content Summary */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <div className="size-1 w-1 h-4 bg-primary rounded-full" />
              <h4 className="text-xs font-black text-slate-900 dark:text-white tracking-widest uppercase">Ringkasan Materi</h4>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 dark:bg-white/[0.02] border border-slate-100 dark:border-white/5">
                <span className="material-symbols-outlined text-lg text-primary">play_circle</span>
                <span className="text-sm font-bold text-slate-600 dark:text-slate-300">
                  {videoLessons.length} Video
                </span>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 dark:bg-white/[0.02] border border-slate-100 dark:border-white/5">
                <span className="material-symbols-outlined text-lg text-blue-400">article</span>
                <span className="text-sm font-bold text-slate-600 dark:text-slate-300">
                  {textLessons.length} Artikel
                </span>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 dark:bg-white/[0.02] border border-slate-100 dark:border-white/5">
                <span className="material-symbols-outlined text-lg text-red-400">picture_as_pdf</span>
                <span className="text-sm font-bold text-slate-600 dark:text-slate-300">
                  {pdfLessons.length} PDF
                </span>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 dark:bg-white/[0.02] border border-slate-100 dark:border-white/5">
                <span className="material-symbols-outlined text-lg text-slate-400">description</span>
                <span className="text-sm font-bold text-slate-600 dark:text-slate-300">
                  {lessons.length} Total
                </span>
              </div>
            </div>
          </div>

          {/* Warning if no content */}
          {!hasContent && (
            <div className="rounded-2xl bg-amber-500/10 border border-amber-500/20 p-5 mb-8">
              <div className="flex items-start gap-4">
                <span className="material-symbols-outlined text-amber-500 text-[24px]">warning</span>
                <div>
                  <p className="text-sm text-amber-600 dark:text-amber-400 font-black tracking-tight">Belum Ada Materi</p>
                  <p className="text-xs text-amber-600/80 dark:text-amber-400/80 mt-1 font-medium leading-relaxed">
                    Modul ini belum memiliki materi. Sebaiknya isi materi dulu sebelum diterbitkan.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Info */}
          <div className="rounded-2xl bg-blue-500/10 border border-blue-500/20 p-5">
            <div className="flex items-start gap-4">
              <span className="material-symbols-outlined text-blue-500 text-[24px]">info</span>
              <p className="text-xs font-medium text-blue-600 dark:text-blue-400 leading-relaxed">
                Setelah diterbitkan, modul ini akan terlihat oleh siswa yang terdaftar. Anda tetap bisa mengubah statusnya kembali ke Draft nanti.
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="px-8 py-6 bg-slate-50 dark:bg-white/[0.02] border-t border-slate-100 dark:border-white/5 flex justify-end gap-4">
          <button
            onClick={onClose}
            disabled={isPublishing}
            className="px-6 py-3 text-slate-500 dark:text-slate-400 text-sm font-black tracking-widest hover:text-slate-900 dark:hover:text-white transition-all disabled:opacity-50"
          >
            Batal
          </button>
          <button
            onClick={onPublish}
            disabled={isPublishing}
            className="px-8 py-4 bg-emerald-500 text-white rounded-2xl text-sm font-black tracking-wider hover:scale-[1.02] active:scale-[0.98] transition-all shadow-xl shadow-emerald-500/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3"
          >
            {isPublishing ? (
              <>
                <div className="animate-spin rounded-full size-4 border-2 border-white border-t-transparent" />
                Menerbitkan...
              </>
            ) : (
              <>
                <span className="material-symbols-outlined text-[20px]">publish</span>
                Ya, Terbitkan Modul
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default PublishModuleModal
